import { Injectable } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { SubscriptionService } from "src/app/core/service/subscription.service";
import { CookiesDialogComponent } from "src/app/shared/component/cookies-dialog/cookies-dialog.component";
import { UserCustomerDTO } from "../../schema/Billing/Customer/UserCustomerDTO";
import { CourseApresentationDTO } from "../../schema/Courses/CourseApresentationDTO";
import { CursoTO } from "../../schema/Courses/CursoTO";
import { PurchaseEventDTO } from "../../schema/DataAnalysis/PurchaseEventDTO";
import { SubscriptionDTO } from "../../schema/Subscriptions/SubscriptionDTO";
import { UsuarioTO } from "../../schema/User/UsuarioTO";
import { FacebookPixelService } from "../facebook-pixel/facebook-pixel.service";
import { GoogleAnalyticsService } from "../google-analytics/google-analytics.service";

@Injectable({
    providedIn: "root"
})
export class DataAnalysisService {
    private subscription: SubscriptionDTO;
    private accepted?: boolean = null;

    private googleAnalyticsId: string;
    private pixelId: string;

    constructor(
        private subscriptionService: SubscriptionService,
        private facebookPixelService: FacebookPixelService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private dialog: MatDialog
    ) {
        this.subscriptionService.currentSubscriptionAsync().subscribe((sub) => {
            this.subscription = sub;
            this.pixelId = this.subscription?.dataAnalysisSettings?.facebookPixel?.facebookPixelId;
            this.googleAnalyticsId = this.subscription?.dataAnalysisSettings?.googleAnalytics?.googleAnalyticsId;
            this.accepted = JSON.parse(localStorage.getItem("cookies-accepted"));

            // If already accepted and pixel is active, grant consent
            if (this.accepted && this.pixelId && this.subscription?.dataAnalysisSettings?.facebookPixel?.active) {
                this.facebookPixelService.grantConsent();
            }

            if (this.googleAnalyticsActive()) this.googleAnalyticsService.load();

            const hasCookies =
                this.subscription?.dataAnalysisSettings?.facebookPixel?.active ||
                this.subscription?.dataAnalysisSettings?.googleAnalytics?.active;

            if (!this.accepted && hasCookies && !this.dialog.getDialogById("cookiesDialog")) {
                const config: MatDialogConfig = {
                    hasBackdrop: false,
                    id: "cookiesDialog",
                    position: { bottom: "10px" },
                    panelClass: "cookies-dialog"
                };

                const dialogRef = this.dialog.open(CookiesDialogComponent, config);

                dialogRef.afterClosed().subscribe((result) => {
                    if (!result) {
                        this.pixelId = null;
                        this.googleAnalyticsId = null;
                        localStorage.setItem("cookies-accepted", "false");
                        if (this.subscription?.dataAnalysisSettings?.facebookPixel?.active) {
                            this.facebookPixelService.revokeConsent();
                        }
                        return;
                    }

                    localStorage.setItem("cookies-accepted", "true");
                    this.accepted = true;

                    // Grant consent and track initial page view after acceptance
                    if (this.pixelId && this.subscription?.dataAnalysisSettings?.facebookPixel?.active) {
                        this.facebookPixelService.grantConsent();
                        this.facebookPixelService.trackPageView();
                    }

                    if (this.googleAnalyticsActive()) this.googleAnalyticsService.load();
                });
            }
        });
    }

    pixelActive(): boolean {
        const active =
            this.subscription?.dataAnalysisSettings?.facebookPixel?.active == true &&
            this.accepted === true &&
            !!this.pixelId;

        if (!active) {
            // Uncomment to debug why pixel is not active
            // console.log("Pixel inactive:", {
            //     settingsActive: this.subscription?.dataAnalysisSettings?.facebookPixel?.active,
            //     accepted: this.accepted,
            //     pixelId: this.pixelId,
            //     hostname: window?.location?.hostname
            // });
        }

        // Allow localhost for testing if needed, or check environment
        // Removed localhost check to allow testing if pixel is configured
        return active; // && window?.location?.hostname != "localhost";
    }

    googleAnalyticsActive(): boolean {
        return (
            this.subscription?.dataAnalysisSettings?.googleAnalytics?.active == true &&
            this.accepted === true &&
            !!this.googleAnalyticsId &&
            window?.location?.hostname != "localhost"
        );
    }

    viewDetailCatalog(course: CursoTO): void {
        try {
            if (this.pixelActive())
                this.facebookPixelService.viewContent(
                    "Catálogo",
                    course?.titulo ?? "",
                    course?.type ?? "product",
                    Number(course?.fixedPrice) || 0,
                    course?.id ? [course.id] : []
                );

            if (this.googleAnalyticsActive())
                this.googleAnalyticsService.ViewItem(
                    Number(course?.fixedPrice) || 0,
                    course?.id ?? "",
                    course?.titulo ?? "",
                    course?.categoria ?? "",
                    "Catálogo",
                    null
                );
        } catch (error) {
            console.log("erro ao enviar evento viewDetailCatalog " + error);
        }
    }

    viewCoursePresentation(course: CourseApresentationDTO, userEmail: string): void {
        try {
            if (this.pixelActive())
                this.facebookPixelService.viewContent(
                    "Apresentação",
                    course?.titulo ?? "",
                    course?.type ?? "product",
                    Number(course?.fixedPrice) || 0,
                    course?.id ? [course.id] : []
                );

            if (this.googleAnalyticsActive())
                this.googleAnalyticsService.ViewItem(
                    Number(course?.fixedPrice) || 0,
                    course?.id ?? "",
                    course?.titulo ?? "",
                    course?.categoria ?? "",
                    "Apresentação",
                    userEmail ?? ""
                );
        } catch (error) {
            console.log("erro ao enviar evento viewCoursePresentation " + error);
        }
    }

    login(email: string, usernames: string[], name: string, method?: string): void {
        try {
            if (this.pixelActive())
                this.facebookPixelService.Login(
                    email ?? "",
                    Array.isArray(usernames) ? usernames : [],
                    name ?? "",
                    method
                );

            if (this.googleAnalyticsActive())
                this.googleAnalyticsService.Login(
                    email ?? "",
                    Array.isArray(usernames) ? usernames : [],
                    name ?? "",
                    method
                );
        } catch (error) {
            console.log("erro ao enviar evento login " + error);
        }
    }

    contact(): void {
        if (this.pixelActive()) this.facebookPixelService.Contact();
    }

    viewCourseContent(course: CursoTO, currentLesson: string, userEmail: string): void {
        try {
            if (this.pixelActive())
                this.facebookPixelService.viewContent(
                    "Aulas",
                    course?.titulo ?? "",
                    course?.type ?? "product",
                    Number(course?.fixedPrice) || 0,
                    course?.id ? [course.id] : [],
                    currentLesson ?? ""
                );

            if (this.googleAnalyticsActive())
                this.googleAnalyticsService.ViewCourseContent(
                    course?.titulo ?? "",
                    course?.id ?? "",
                    course?.categoria ?? "",
                    currentLesson ?? "",
                    userEmail ?? ""
                );
        } catch (error) {
            console.log("erro ao enviar evento viewCourseContent " + error);
        }
    }

    freeCourseRegistration(course: CourseApresentationDTO, user: UsuarioTO): void {
        try {
            if (this.pixelActive())
                this.facebookPixelService.freeCourseRegistration(
                    course?.titulo ?? "",
                    course?.id ?? "",
                    user?.email ?? "",
                    ((user?.nome ?? "") + " " + (user?.sobrenome ?? "")).trim()
                );

            if (this.googleAnalyticsActive())
                this.googleAnalyticsService.freeCourseRegistration(
                    course?.titulo ?? "",
                    course?.id ?? "",
                    user?.email ?? "",
                    ((user?.nome ?? "") + " " + (user?.sobrenome ?? "")).trim()
                );
        } catch (error) {
            console.log("erro ao enviar evento freeCourseRegistration " + error);
        }
    }

    searchCatolog(search: string): void {
        try {
            if (this.pixelActive()) this.facebookPixelService.Search("Catálogo", search);
            if (this.googleAnalyticsActive()) this.googleAnalyticsService.Search("Catálogo", search);
        } catch (error) {
            console.log("erro ao enviar evento searchCatolog " + error);
        }
    }

    searchAprender(search: string, userEmail: string): void {
        try {
            if (this.pixelActive()) this.facebookPixelService.Search("Aprender", search, userEmail);
            if (this.googleAnalyticsActive()) this.googleAnalyticsService.Search("Aprender", search, userEmail);
        } catch (error) {
            console.log("erro ao enviar evento searchAprender " + error);
        }
    }

    purchaseProduct(data: PurchaseEventDTO): void {
        try {
            if (this.pixelActive()) {
                this.facebookPixelService.Purchase(
                    data.productTitle ?? "",
                    "product",
                    "BRL",
                    Number(data.billAmount) || 0,
                    {
                        userName: data.customerName ?? "",
                        email: data.customerEmail ?? "",
                        billId: data.billId ?? "",
                        coupon: data.coupon ?? undefined
                    }
                );
            }

            if (this.googleAnalyticsActive()) {
                this.googleAnalyticsService.Purchase(
                    data.billId ?? "",
                    Number(data.billAmount) || 0,
                    data.productId ?? "",
                    data.productTitle?? "",
                    "product",
                    data.customerEmail ?? "",
                    data.coupon ?? undefined
                );
            }
        } catch (error) {
            console.log("erro ao enviar evento purchaseProduct " + error);
        }
    }

    initiateCheckout(course: CursoTO): void {
        try {
            if (this.pixelActive())
                this.facebookPixelService.InitiateCheckout(
                    course?.titulo ?? "",
                    "product",
                    course?.id ? [course.id] : [],
                    Number(course?.fixedPrice) || 0,
                    "BRL"
                );
            if (this.googleAnalyticsActive())
                this.googleAnalyticsService.BeginCheckout(
                    Number(course?.fixedPrice) || 0,
                    course?.id ?? "",
                    course?.titulo ?? "",
                    "product"
                );
        } catch (error) {
            console.log("erro ao enviar evento initiateCheckout " + error);
        }
    }

    AddPaymentInfo(method: string, course: CursoTO, customer: UserCustomerDTO, coupon?: string): void {
        try {
            if (this.pixelActive())
                this.facebookPixelService.AddPaymentInfo(method ?? "", {
                    userName: customer?.name ?? "",
                    email: customer?.email ?? "",
                    productId: course?.id ?? "",
                    productName: course?.titulo ?? ""
                });

            if (this.googleAnalyticsActive())
                this.googleAnalyticsService.AddPaymentInfo(
                    Number(course?.fixedPrice) || 0,
                    method ?? "",
                    course?.id ?? "",
                    course?.titulo ?? "",
                    "product",
                    customer?.email ?? "",
                    coupon ?? undefined
                );
        } catch (error) {
            console.log("erro ao enviar evento AddPaymentInfo " + error);
        }
    }

    CompleteRegistration(registrationBy: string, name: string, userEmail: string, status: boolean): void {
        try {
            if (this.pixelActive())
                this.facebookPixelService.CompleteRegistration(status, registrationBy, {
                    name: name,
                    email: userEmail
                });

            if (this.googleAnalyticsActive())
                this.googleAnalyticsService.CompleteRegistration(status, registrationBy, name, userEmail);
        } catch (error) {
            console.log("erro ao enviar evento CompleteRegistration " + error);
        }
    }
}
